"use client";

// ============================================================
// CLUBHUB – Dokumente: Vorlagen
// ============================================================
// Die eigenen Vordrucke des Vereins — Protokoll, Einladung,
// Vollmacht, Aufnahmeantrag. Eine Vorlage ist eine Datei mit
// Platzhaltern; „Verwenden“ fuellt sie (Vereinsname, Datum, bei
// Bedarf ein Mitglied) und legt das fertige Dokument gleich in den
// richtigen Ordner. In ClubDesk lagen Vorlagen getrennt von den
// Laufwerken, und das Ergebnis musste man herunter- und wieder
// hochladen.
//
// Die Eintraege im Musterbestand sind Platzhalter mit eigenen
// Titeln — keine Vorlagentexte aus ClubDesk.
// ============================================================

import { useMemo, useRef, useState } from "react";
import Link from "next/link";
import { ChevronLeft, Download, FilePlus2, Pencil, Trash2, Wand2 } from "lucide-react";
import {
  Button,
  Checkbox,
  confirmDialog,
  DateInput,
  Dialog,
  DialogCancel,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogForm,
  DialogHeader,
  DialogNotice,
  DialogSubmit,
  DialogTitle,
  DropdownItem,
  DropdownMenu,
  DropdownSeparator,
  EmptyState,
  FieldLabel,
  Input,
  Select,
  Textarea,
  toast,
} from "@/components/ui";
import { MehrKnopf } from "@/components/ui/zeichen-knopf";
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Filterkopf, Fuge, Listenkoerper, Listentabelle, Luecke, Zaehlknopf, Zaehlschiene } from "@/components/liste";
import { KontaktAuswahl } from "@/components/mitglieder/kontakt-auswahl";
import { mockContact } from "@/lib/mock/core";
import { cn, formatDate } from "@/lib/utils";
import { formatDecimal } from "@/lib/number-format";
import {
  endung,
  endungsLabel,
  formatBytes,
  jetztIso,
  kinderVon,
  mimeFuer,
  namePruefen,
  normalisiert,
  pfadText,
  slug,
} from "@/lib/dokumente/format";
import type { DocumentView, TemplateKind, TemplateView } from "@/lib/dokumente/views";
import { neueId } from "./ablage-zustand";
import { useAblage } from "./ablage-kontext";
import { Dateisuche } from "./ablage-filterkopf";
import { Daumen } from "./vorschau";

export const VORLAGE_ART: Record<TemplateKind, string> = {
  PROTOKOLL: "Protokoll",
  EINLADUNG: "Einladung",
  ANTRAG: "Antrag",
  VOLLMACHT: "Vollmacht",
  EINVERSTAENDNIS: "Einverständnis",
  VEREINBARUNG: "Vereinbarung",
  QUITTUNG: "Quittung",
};

const ART_REIHE = Object.keys(VORLAGE_ART) as TemplateKind[];
const ERLAUBTE_ENDUNGEN = ["docx", "odt", "pdf", "xlsx", "ods"];

export function gefilterteVorlagen(vorlagen: TemplateView[], q: string, art: string): TemplateView[] {
  const n = normalisiert(q.trim());
  return vorlagen
    .filter((v) => !n || normalisiert(`${v.title} ${v.description} ${v.fields.join(" ")}`).includes(n))
    .filter((v) => !art || v.kind === art)
    .sort((a, b) => a.title.localeCompare(b.title, "de"));
}

export function VorlagenFilterkopf() {
  const { bestand, ansicht, setzenHier } = useAblage();
  const gefunden = gefilterteVorlagen(bestand.templates, ansicht.vq, "");
  const zaehler = new Map<string, number>();
  for (const v of gefunden) zaehler.set(v.kind, (zaehler.get(v.kind) ?? 0) + 1);

  return (
    <Filterkopf
      oben={
        <>
          <Dateisuche wert={ansicht.vq} onWert={(q) => setzenHier({ vq: q || null })} platzhalter="Vorlage, Feld …" beschriftung="Vorlagen durchsuchen" />
          <Luecke />
          <Zaehlschiene titel="Art">
            <Zaehlknopf label="Alle" sammel anzahl={gefunden.length} aktiv={!ansicht.vart} onClick={() => setzenHier({ vart: null })} />
            <Fuge />
            {ART_REIHE.filter((a) => (zaehler.get(a) ?? 0) > 0 || ansicht.vart === a).map((a) => (
              <Zaehlknopf
                key={a}
                label={VORLAGE_ART[a]}
                anzahl={zaehler.get(a) ?? 0}
                aktiv={ansicht.vart === a}
                onClick={() => setzenHier({ vart: ansicht.vart === a ? null : a })}
              />
            ))}
          </Zaehlschiene>
        </>
      }
    />
  );
}

/**
 * Mit Sitzung sind Vorlagen noch nicht angeschlossen (Ausfuellen der
 * Platzhalter fehlt) — gelesen wird `DocumentTemplate`, geschrieben noch nicht.
 */
export const VORLAGEN_SPAETER = "Vorlagen lassen sich noch nicht speichern — das kommt in einem nächsten Schritt. Bis dahin bitte als Datei in einen Ordner hochladen.";

function VorlagenMenue({ vorlage }: { vorlage: TemplateView }) {
  const { daten, oeffnen, aendern } = useAblage();

  async function loeschen() {
    if (daten.quelle === "datenbank") {
      toast.message(VORLAGEN_SPAETER);
      return;
    }
    const ok = await confirmDialog({
      title: `Vorlage „${vorlage.title}“ löschen?`,
      description: "Dokumente, die schon daraus entstanden sind, bleiben in ihren Ordnern.",
      confirmLabel: "Löschen",
      danger: true,
    });
    if (!ok) return;
    // BACKEND: deleteTemplate(id) — Datei im Bucket entfernen.
    aendern((b) => ({ ...b, templates: b.templates.filter((t) => t.id !== vorlage.id) }));
    toast.success("Vorlage gelöscht", {
      action: { label: "Rückgängig", onClick: () => aendern((b) => ({ ...b, templates: [...b.templates, vorlage] })) },
    });
  }

  return (
    <DropdownMenu trigger={<MehrKnopf groesse="klein" label={`${vorlage.title}: weitere Aktionen`} />}>
      <DropdownItem onClick={() => oeffnen({ art: "vorlage-verwenden", vorlageId: vorlage.id })}>
        <Wand2 className="h-4 w-4" aria-hidden />
        Verwenden…
      </DropdownItem>
      <DropdownItem
        onClick={() =>
          daten.quelle === "datenbank"
            ? toast.message(VORLAGEN_SPAETER)
            : toast.success(`„${vorlage.fileName}“ wird geladen`, { description: "Musterdaten — im Betrieb kommt hier die Datei aus dem Speicher." })
        }
      >
        <Download className="h-4 w-4" aria-hidden />
        Leer herunterladen
      </DropdownItem>
      <DropdownSeparator />
      <DropdownItem onClick={() => oeffnen({ art: "vorlage-bearbeiten", vorlageId: vorlage.id })}>
        <Pencil className="h-4 w-4" aria-hidden />
        Bearbeiten…
      </DropdownItem>
      <DropdownSeparator />
      <DropdownItem variant="danger" onClick={() => void loeschen()}>
        <Trash2 className="h-4 w-4" aria-hidden />
        Löschen
      </DropdownItem>
    </DropdownMenu>
  );
}

export function VorlagenInhalt() {
  const { bestand, ansicht, oeffnen, setzenHier } = useAblage();
  const liste = gefilterteVorlagen(bestand.templates, ansicht.vq, ansicht.vart);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="border-border-subtle bg-bg-primary flex items-center gap-3 border-b px-4 py-2.5">
        {/* Auf dem Telefon fehlt der Baum — der Weg zurueck in die Ablage steht hier. */}
        <Link href="/dokumente" className="text-body-sm text-text-tertiary hover:text-text-primary flex shrink-0 items-center gap-1 md:hidden">
          <ChevronLeft className="h-4 w-4" aria-hidden />
          Ablage
        </Link>
        <p className="text-body-sm text-text-secondary min-w-0 flex-1 max-md:hidden">
          Eigene Vordrucke des Vereins. Beim Verwenden füllt clubhub die Felder aus und legt das Ergebnis in einen Ordner.
        </p>
      </div>
      {liste.length === 0 ? (
        <Listenkoerper className="p-5">
          <EmptyState
            icon={FilePlus2}
            title={bestand.templates.length === 0 ? "Noch keine Vorlage" : "Keine Vorlage passt"}
            description={
              bestand.templates.length === 0
                ? "Lade einen eigenen Vordruck hoch — Word, OpenDocument oder PDF. Platzhalter wie {Vereinsname} füllt clubhub später aus."
                : "Ändere die Suche oder die Art."
            }
            action={
              bestand.templates.length === 0 ? (
                <Button onClick={() => oeffnen({ art: "vorlage-bearbeiten", vorlageId: null })}>Vorlage hochladen</Button>
              ) : (
                <Button variant="secondary" onClick={() => setzenHier({ vq: null, vart: null })}>
                  Filter zurücksetzen
                </Button>
              )
            }
          />
        </Listenkoerper>
      ) : (
        <Listentabelle>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>VORLAGE</TableHead>
              <TableHead className="hidden md:table-cell">ART</TableHead>
              <TableHead className="hidden lg:table-cell">FELDER</TableHead>
              <TableHead className="hidden md:table-cell">STAND</TableHead>
              <TableHead className="hidden text-right sm:table-cell">VERWENDET</TableHead>
              <TableHead className="w-px">
                <span className="sr-only">Aktionen</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {liste.map((v) => (
              <TableRow key={v.id}>
                <TableCell className="w-full max-w-0">
                  <button
                    type="button"
                    onClick={() => oeffnen({ art: "vorlage-verwenden", vorlageId: v.id })}
                    className="flex w-full min-w-0 items-center gap-3 text-left after:absolute after:inset-0 after:content-['']"
                  >
                    <Daumen datei={{ ...LEER, name: v.fileName, mimeType: v.mimeType }} />
                    <span className="min-w-0">
                      <span className="text-text-primary block truncate font-medium">{v.title}</span>
                      <span className="text-caption text-text-tertiary block truncate">{v.description}</span>
                      <span className="text-caption text-text-tertiary block truncate tabular-nums md:hidden">
                        {VORLAGE_ART[v.kind]} · {endungsLabel(v.fileName)} · {formatDecimal(v.usedCount)}-mal verwendet
                      </span>
                    </span>
                  </button>
                </TableCell>
                <TableCell className="text-text-secondary hidden whitespace-nowrap md:table-cell">
                  {VORLAGE_ART[v.kind]}
                  <span className="text-caption text-text-tertiary block">{endungsLabel(v.fileName)} · {formatBytes(v.size)}</span>
                </TableCell>
                <TableCell className="hidden lg:table-cell">
                  <div className="flex max-w-64 flex-wrap gap-1">
                    {v.fields.slice(0, 3).map((f) => (
                      <span key={f} className="bg-bg-tertiary text-caption text-text-secondary rounded px-1.5 py-0.5">
                        {f}
                      </span>
                    ))}
                    {v.fields.length > 3 && <span className="text-caption text-text-tertiary tabular-nums">+{v.fields.length - 3}</span>}
                  </div>
                </TableCell>
                <TableCell className="text-text-secondary hidden tabular-nums md:table-cell">
                  {formatDate(v.updatedAt)}
                  {v.updatedBy && <span className="text-caption text-text-tertiary block">{v.updatedBy.name}</span>}
                </TableCell>
                <TableCell className="text-text-secondary hidden text-right tabular-nums sm:table-cell">
                  {formatDecimal(v.usedCount)}-mal
                  {v.lastUsedAt && <span className="text-caption text-text-tertiary block">zuletzt {formatDate(v.lastUsedAt)}</span>}
                </TableCell>
                <TableCell className="relative z-[1] w-px py-0">
                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="secondary" className="max-sm:hidden" onClick={() => oeffnen({ art: "vorlage-verwenden", vorlageId: v.id })}>
                      Verwenden
                    </Button>
                    <VorlagenMenue vorlage={v} />
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Listentabelle>
      )}
      <p className="text-caption text-text-tertiary border-border-subtle bg-bg-primary border-t px-4 py-2">
        Verwenden darf jeder mit Zugriff auf Dokumente. Vorlagen ändern dürfen Vorstand und Verwaltung.
      </p>
    </div>
  );
}

/** Fuer `Daumen` — eine Vorlage ist keine Datei der Ablage, zeigt sich aber so. */
const LEER: DocumentView = {
  id: "",
  folderId: null,
  name: "",
  description: null,
  storageKey: "",
  size: 0,
  mimeType: "",
  checksum: null,
  version: 1,
  uploadedBy: null,
  createdAt: "",
  updatedAt: "",
  versions: [],
  shares: [],
  width: null,
  height: null,
  pages: null,
  motiv: null,
};

// ── Verwenden ────────────────────────────────────────────────
export function VorlageVerwendenDialog({ vorlageId, onClose }: { vorlageId: string; onClose: () => void }) {
  const { bestand, daten, aendern, setzen } = useAblage();
  const vorlage = bestand.templates.find((t) => t.id === vorlageId);
  const optionen = useMemo(
    () =>
      bestand.folders
        .filter((f) => f.parentId !== null)
        .map((f) => ({ value: f.id, label: pfadText(f.id, bestand.folders) }))
        .sort((a, b) => a.label.localeCompare(b.label, "de")),
    [bestand.folders],
  );
  // Nur ein Ordner, den es (noch) gibt, ist ein Startwert.
  const [ordner, setOrdner] = useState(
    vorlage?.defaultFolderId && bestand.folders.some((f) => f.id === vorlage.defaultFolderId) ? vorlage.defaultFolderId : "",
  );
  const [datum, setDatum] = useState(daten.today);
  const [mitglied, setMitglied] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [nameAnfassen, setNameAnfassen] = useState(false);
  const [fehler, setFehler] = useState<{ feld: string; text: string } | null>(null);
  if (!vorlage) return null;

  const kontakt = mitglied ? mockContact(mitglied) : undefined;
  const e = endung(vorlage.fileName);
  const vorschlag = `${datum} ${vorlage.title}${kontakt ? ` ${kontakt.firstName ?? ""} ${kontakt.lastName ?? kontakt.displayName}`.trimEnd() : ""}`;
  const basis = nameAnfassen ? name : vorschlag;

  const werte: Record<string, string> = {
    Vereinsname: daten.clubName,
    Datum: formatDate(datum),
    Mitglied: kontakt ? `${kontakt.firstName ?? ""} ${kontakt.lastName ?? kontakt.displayName}`.trim() : "—",
    Mitgliedsnummer: kontakt?.memberNumber ?? "—",
    "Anschrift Mitglied": kontakt ? `${kontakt.street ?? ""}, ${kontakt.zip ?? ""} ${kontakt.city ?? ""}` : "—",
    Geburtsdatum: kontakt?.birthDate ? formatDate(kontakt.birthDate) : "—",
  };

  return (
    <Dialog open onClose={onClose} size="wide">
      <DialogForm
        refresh={false}
        action={() => {
          if (daten.quelle === "datenbank") return { error: VORLAGEN_SPAETER };
          if (vorlage.needsContact && !mitglied) return { error: "Bitte ein Mitglied wählen.", field: "mitglied" };
          if (!ordner || !bestand.folders.some((f) => f.id === ordner)) return { error: "Bitte einen Ordner wählen.", field: "ordner" };
          const dateiname = e ? `${basis.trim()}.${e}` : basis.trim();
          const vorhanden = bestand.documents.filter((d) => d.folderId === ordner).map((d) => d.name);
          const f = basis.trim() ? namePruefen(dateiname, vorhanden, "Datei") : "Bitte einen Namen eingeben.";
          if (f) return { error: f, field: "name" };
          const id = neueId("d");
          const jetzt = jetztIso();
          const storageKey = `${daten.storage.pathPrefix ?? ""}${id}/v1/${slug(dateiname)}`;
          const mimeType = mimeFuer(dateiname);
          const size = Math.round(vorlage.size * 1.08);
          // BACKEND: fillTemplate(vorlageId, { folderId, name, datum, contactId }) — Platzhalter
          // ersetzen (docx-templates bzw. PDF-Formularfelder), Ergebnis in den Bucket, Document anlegen.
          aendern((b) => ({
            ...b,
            documents: [
              ...b.documents,
              {
                ...LEER,
                id,
                folderId: ordner,
                name: dateiname,
                description: `Aus der Vorlage „${vorlage.title}“ erstellt.`,
                storageKey,
                size,
                mimeType,
                uploadedBy: daten.me,
                createdAt: jetzt,
                updatedAt: jetzt,
                versions: [{ version: 1, storageKey, size, mimeType, uploadedBy: daten.me, createdAt: jetzt, note: `Aus Vorlage „${vorlage.title}“` }],
                pages: e === "xlsx" ? null : 2,
              },
            ],
            templates: b.templates.map((t) => (t.id === vorlage.id ? { ...t, usedCount: t.usedCount + 1, lastUsedAt: jetzt } : t)),
          }));
          return { success: true as const, id, dateiname };
        }}
        onSuccess={(r) => {
          if (!("id" in r)) return;
          toast.success(`„${r.dateiname}“ liegt in ${pfadText(ordner, bestand.folders)}`, {
            action: { label: "Öffnen", onClick: () => setzen({ ordner, datei: r.id }, true) },
          });
        }}
        onError={(text, r) => setFehler({ feld: (r as { field?: string } | undefined)?.field ?? "", text })}
      >
        <DialogHeader>
          <DialogTitle>{vorlage.title}</DialogTitle>
          <DialogDescription>{vorlage.description}</DialogDescription>
        </DialogHeader>
        <DialogContent>
          <div className="grid grid-cols-1 gap-x-10 gap-y-4 lg:grid-cols-2">
            <div className="min-w-0 space-y-4">
              {vorlage.needsContact && (
                <div className="space-y-1.5">
                  <FieldLabel>Mitglied</FieldLabel>
                  <KontaktAuswahl
                    value={mitglied}
                    onChange={(id) => {
                      setMitglied(id);
                      if (fehler?.feld === "mitglied") setFehler(null);
                    }}
                    status={["ACTIVE", "PASSIVE", "HONORARY"]}
                    placeholder="Mitglied suchen"
                  />
                  {fehler?.feld === "mitglied" && <p className="text-caption text-danger">{fehler.text}</p>}
                </div>
              )}
              <div className="space-y-1.5">
                <FieldLabel htmlFor="vorlage-datum">Datum</FieldLabel>
                <DateInput
                  id="vorlage-datum"
                  value={datum}
                  onChange={(ev) => setDatum(ev.target.value || daten.today)}
                  className="bg-bg-primary text-body-sm text-text-primary border-border flex h-10 w-full rounded-lg border px-3"
                />
              </div>
              <Select
                label="Ablegen in"
                value={ordner}
                onChange={(v) => {
                  setOrdner(v);
                  if (fehler?.feld === "ordner") setFehler(null);
                }}
                options={optionen}
                placeholder="Ordner wählen"
                searchable
                error={fehler?.feld === "ordner" ? fehler.text : undefined}
              />
              <div className="flex items-end gap-2">
                <Input
                  label="Dateiname"
                  value={basis}
                  onChange={(ev) => {
                    setNameAnfassen(true);
                    setName(ev.target.value);
                    if (fehler?.feld === "name") setFehler(null);
                  }}
                  error={fehler?.feld === "name" ? fehler.text : undefined}
                  wrapperClassName="min-w-0 flex-1"
                  autoComplete="off"
                />
                <span className={cn("text-body-sm text-text-tertiary pb-2.5", fehler?.feld === "name" && "pb-8")}>.{e}</span>
              </div>
            </div>
            <div className="min-w-0 space-y-2">
              <FieldLabel>Felder, die clubhub ausfüllt</FieldLabel>
              <dl className="border-border text-body-sm divide-border-subtle divide-y rounded-lg border">
                {vorlage.fields.map((f) => (
                  <div key={f} className="flex items-baseline justify-between gap-4 px-3 py-2">
                    <dt className="text-text-tertiary shrink-0">{f}</dt>
                    <dd className={cn("min-w-0 truncate text-right", werte[f] && werte[f] !== "—" ? "text-text-primary" : "text-text-tertiary")}>
                      {werte[f] ?? "aus Einstellungen › Verein"}
                    </dd>
                  </div>
                ))}
              </dl>
              {ordner && (
                <p className="text-caption text-text-tertiary">
                  Landet in {pfadText(ordner, bestand.folders)}
                  {kinderVon(ordner, bestand.folders).length > 0 ? " (nicht in den Unterordnern)" : ""}.
                </p>
              )}
            </div>
          </div>
        </DialogContent>
        <DialogFooter>
          <DialogCancel />
          <DialogSubmit>
            <Wand2 className="h-4 w-4" aria-hidden />
            Ausfüllen und ablegen
          </DialogSubmit>
        </DialogFooter>
      </DialogForm>
    </Dialog>
  );
}

// ── Anlegen und bearbeiten ───────────────────────────────────
export function VorlageBearbeitenDialog({ vorlageId, onClose }: { vorlageId: string | null; onClose: () => void }) {
  const { bestand, daten, aendern } = useAblage();
  const vorlage = vorlageId ? bestand.templates.find((t) => t.id === vorlageId) : undefined;
  const [titel, setTitel] = useState(vorlage?.title ?? "");
  const [art, setArt] = useState<TemplateKind>(vorlage?.kind ?? "PROTOKOLL");
  const [beschreibung, setBeschreibung] = useState(vorlage?.description ?? "");
  const [mitglied, setMitglied] = useState(vorlage?.needsContact ?? false);
  const [datei, setDatei] = useState<File | null>(null);
  const [fehler, setFehler] = useState<{ feld: string; text: string } | null>(null);
  const feld = useRef<HTMLInputElement>(null);

  return (
    <Dialog open onClose={onClose}>
      <DialogForm
        refresh={false}
        action={() => {
          if (daten.quelle === "datenbank") return { error: VORLAGEN_SPAETER };
          const t = titel.trim();
          if (!t) return { error: "Bitte einen Titel eingeben.", field: "titel" };
          if (bestand.templates.some((v) => v.id !== vorlageId && v.title.toLocaleLowerCase("de") === t.toLocaleLowerCase("de"))) {
            return { error: "Eine Vorlage mit diesem Titel gibt es schon.", field: "titel" };
          }
          if (!vorlage && !datei) return { error: "Bitte die Datei der Vorlage wählen.", field: "datei" };
          if (datei && !ERLAUBTE_ENDUNGEN.includes(endung(datei.name).toLowerCase())) {
            return { error: "Vorlagen sind Word, OpenDocument, Excel oder PDF.", field: "datei" };
          }
          if (datei && datei.size === 0) return { error: "Die Datei ist leer.", field: "datei" };
          const jetzt = jetztIso();
          // BACKEND: upsertTemplate({ title, kind, description, needsContact }, datei) — Datei in den
          // Bucket, Platzhalter `{…}` auslesen und als `fields` speichern.
          aendern((b) => {
            if (vorlage) {
              return {
                ...b,
                templates: b.templates.map((v) =>
                  v.id === vorlage.id
                    ? {
                        ...v,
                        title: t,
                        kind: art,
                        description: beschreibung.trim(),
                        needsContact: mitglied,
                        ...(datei ? { fileName: datei.name, mimeType: mimeFuer(datei.name, datei.type), size: datei.size } : {}),
                        updatedAt: jetzt,
                        updatedBy: daten.me,
                      }
                    : v,
                ),
              };
            }
            return {
              ...b,
              templates: [
                ...b.templates,
                {
                  id: neueId("v"),
                  title: t,
                  kind: art,
                  description: beschreibung.trim(),
                  fileName: datei!.name,
                  mimeType: mimeFuer(datei!.name, datei!.type),
                  size: datei!.size,
                  fields: mitglied ? ["Vereinsname", "Datum", "Mitglied"] : ["Vereinsname", "Datum"],
                  needsContact: mitglied,
                  defaultFolderId: null,
                  updatedAt: jetzt,
                  updatedBy: daten.me,
                  usedCount: 0,
                  lastUsedAt: null,
                },
              ],
            };
          });
          return { success: true as const };
        }}
        success={vorlage ? "Vorlage gespeichert" : `Vorlage „${titel.trim()}“ angelegt`}
        onError={(text, r) => setFehler({ feld: (r as { field?: string } | undefined)?.field ?? "", text })}
      >
        <DialogHeader>
          <DialogTitle>{vorlage ? "Vorlage bearbeiten" : "Neue Vorlage"}</DialogTitle>
          <DialogDescription>Ein eigener Vordruck als Word-, OpenDocument- oder PDF-Datei. Platzhalter wie {"{Vereinsname}"} füllt clubhub beim Verwenden.</DialogDescription>
        </DialogHeader>
        <DialogContent>
          <Input
            label="Titel"
            value={titel}
            onChange={(ev) => {
              setTitel(ev.target.value);
              if (fehler?.feld === "titel") setFehler(null);
            }}
            placeholder="z. B. Protokoll Jugendausschuss"
            error={fehler?.feld === "titel" ? fehler.text : undefined}
            autoComplete="off"
          />
          <Select label="Art" value={art} onChange={(v) => setArt(v as TemplateKind)} options={ART_REIHE.map((a) => ({ value: a, label: VORLAGE_ART[a] }))} />
          <Textarea label="Beschreibung" value={beschreibung} onChange={(ev) => setBeschreibung(ev.target.value)} rows={2} placeholder="Wofür, und was ist zu beachten?" />
          <Checkbox
            label="Braucht die Angaben eines Mitglieds"
            hint="Beim Verwenden wird dann ein Mitglied gewählt — Vollmacht, Einverständnis, Vereinbarung."
            checked={mitglied}
            onChange={(ev) => setMitglied(ev.target.checked)}
          />
          <div className="space-y-1.5">
            <FieldLabel>Datei</FieldLabel>
            <input
              ref={feld}
              type="file"
              accept=".docx,.odt,.pdf,.xlsx,.ods"
              className="hidden"
              onChange={(ev) => {
                setDatei(ev.target.files?.[0] ?? null);
                if (fehler?.feld === "datei") setFehler(null);
              }}
            />
            <div className={cn("flex items-center gap-3 rounded-lg border px-3 py-2", fehler?.feld === "datei" ? "border-danger" : "border-border")}>
              <span className="text-body-sm text-text-secondary min-w-0 flex-1 truncate">
                {datei ? datei.name : vorlage ? `${vorlage.fileName} · ${formatBytes(vorlage.size)}` : "Noch keine Datei gewählt"}
              </span>
              <Button type="button" size="sm" variant="outline" onClick={() => feld.current?.click()}>
                {datei || vorlage ? "Ersetzen" : "Datei wählen"}
              </Button>
            </div>
            {fehler?.feld === "datei" && <p className="text-caption text-danger">{fehler.text}</p>}
          </div>
          {vorlage && (
            <DialogNotice>
              Schon {formatDecimal(vorlage.usedCount)}-mal verwendet. Eine neue Datei gilt ab jetzt — fertige Dokumente bleiben, wie sie sind.
            </DialogNotice>
          )}
        </DialogContent>
        <DialogFooter>
          <DialogCancel />
          <DialogSubmit>{vorlage ? "Speichern" : "Vorlage anlegen"}</DialogSubmit>
        </DialogFooter>
      </DialogForm>
    </Dialog>
  );
}
